import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import SummarizeIcon from '@mui/icons-material/Summarize';
import MapIcon from '@mui/icons-material/Map';
import RefreshIcon from '@mui/icons-material/Refresh';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { apiService, CanvasPromptRunRecordModel, PromptLogEntry } from '../services/api';

// Helper to safely format JSON with fallback
const formatResponse = (responseText: string): string => {
  try {
    const parsed = JSON.parse(responseText);
    return JSON.stringify(parsed, null, 2);
  } catch {
    // If parsing fails, return original
    return responseText;
  }
};

// Categorize logs by template type
type LogCategory = 'all' | 'summary' | 'mapping' | 'regeneration' | 'question';

const getCategoryFromLogType = (logType: string): LogCategory => {
  const lowerType = logType.toLowerCase();
  if (lowerType.includes('semantic') || lowerType.includes('summary') || lowerType.includes('summarizer')) {
    return 'summary';
  }
  if (lowerType.includes('regenerat')) {
    return 'regeneration';
  }
  if (lowerType.includes('question') || lowerType.includes('chat') || lowerType.includes('advisor')) {
    return 'question';
  }
  if (lowerType.includes('mapping') || lowerType.includes('generation')) {
    return 'mapping';
  }
  return 'mapping'; // Default to mapping
};

const getCategoryFromTemplate = (template: string): LogCategory => {
  const lowerTemplate = template.toLowerCase();
  if (lowerTemplate.includes('semantic') || lowerTemplate.includes('summary')) {
    return 'summary';
  }
  if (lowerTemplate.includes('regenerat')) {
    return 'regeneration';
  }
  if (lowerTemplate.includes('question') || lowerTemplate.includes('chat') || lowerTemplate.includes('advisor')) {
    return 'question';
  }
  if (lowerTemplate.includes('mapping') || lowerTemplate.includes('generation')) {
    return 'mapping';
  }
  return 'mapping'; // Default to mapping
};

const getCategoryConfig = (category: LogCategory) => {
  switch (category) {
    case 'summary':
      return { label: 'Source Summarizer', icon: <SummarizeIcon fontSize="small" />, color: 'info' as const };
    case 'mapping':
      return { label: 'AI Mapping', icon: <MapIcon fontSize="small" />, color: 'primary' as const };
    case 'regeneration':
      return { label: 'Regeneration', icon: <RefreshIcon fontSize="small" />, color: 'warning' as const };
    case 'question':
      return { label: 'Q&A', icon: <QuestionAnswerIcon fontSize="small" />, color: 'secondary' as const };
    default:
      return { label: 'All', icon: <AllInclusiveIcon fontSize="small" />, color: 'default' as const };
  }
};

const PromptLogViewer: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const theme = useTheme();

  // Fetch from the dedicated prompt logs API
  const { data: logsData, isLoading, error } = useQuery(
    ['promptLogs', batchId],
    () => batchId ? apiService.getPromptLogs(batchId) : Promise.resolve(null),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      enabled: !!batchId,
    }
  );

  // Also try to get canvas graph for backward compatibility (may 404 if mapping not done)
  const { data: graph } = useQuery(
    ['canvasGraph', batchId],
    () => batchId ? apiService.getCanvasGraph(batchId) : Promise.resolve(null),
    {
      refetchOnWindowFocus: false,
      retry: false, // Don't retry - it's optional
      enabled: !!batchId,
    }
  );

  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [expandAll, setExpandAll] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<LogCategory>('all');

  if (!batchId) {
    return (
      <Box p={2}>
        <Typography color="error">Batch ID is missing.</Typography>
      </Box>
    );
  }

  const handleExpandClick = (runId: string) => {
    setExpandedRunId(expandedRunId === runId ? null : runId);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">Failed to load prompt logs.</Typography>
      </Box>
    );
  }

  // Combine logs from the new API and optionally from canvas graph (for backward compat)
  const promptLogs: PromptLogEntry[] = logsData?.logs || [];
  const canvasPromptRuns = graph?.metadata?.prompt_runs || [];
  
  // Convert canvas prompt runs to our PromptLogEntry format
  const convertedCanvasLogs: PromptLogEntry[] = canvasPromptRuns.map((run: CanvasPromptRunRecordModel) => ({
    log_id: run.llm_run_id,
    batch_id: batchId,
    llm_run_id: run.llm_run_id,
    log_type: getCategoryFromTemplate(run.prompt_template),
    prompt_template: run.prompt_template,
    model_id: run.model_id,
    prompt_text: run.prompt_text,
    response_text: run.response_text,
    token_usage: run.token_usage,
    metadata: { all_prompts: run.all_prompts },
    created_at: run.generated_at,
  }));
  
  // Merge and dedupe by log_id/llm_run_id AND content fingerprint
  // The backend may store duplicate log entries with different log_ids
  // for the same LLM call (e.g., from retry logic or multi-agent writes).
  const seenIds = new Set<string>();
  const seenFingerprints = new Set<string>();
  const allLogs: PromptLogEntry[] = [];

  // Build a content fingerprint for dedup (same type + template + tokens + metadata = same log)
  const fingerprint = (log: PromptLogEntry): string => {
    const tokens = log.token_usage?.total_tokens || 0;
    const meta = JSON.stringify(log.metadata || {});
    return `${log.log_type}-${log.prompt_template}-${tokens}-${meta}`;
  };

  // Prefer logs from the new API first
  for (const log of promptLogs) {
    const key = log.log_id || log.llm_run_id || '';
    const fp = fingerprint(log);
    if (key && seenIds.has(key)) continue; // exact ID dedup
    if (seenFingerprints.has(fp)) continue; // content dedup
    if (key) seenIds.add(key);
    seenFingerprints.add(fp);
    allLogs.push(log);
  }

  // Add canvas logs that aren't already present
  for (const log of convertedCanvasLogs) {
    const key = log.llm_run_id || '';
    const fp = fingerprint(log);
    if (key && seenIds.has(key)) continue;
    if (seenFingerprints.has(fp)) continue;
    if (key) seenIds.add(key);
    seenFingerprints.add(fp);
    allLogs.push(log);
  }

  if (allLogs.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body1" color="text.secondary">
          No prompt logs available for this batch.
        </Typography>
      </Box>
    );
  }

  // Sort by created_at descending and categorize
  const sortedLogs = [...allLogs].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  // Categorize logs
  const categorizedLogs = sortedLogs.map((log) => ({
    ...log,
    category: getCategoryFromLogType(log.log_type),
  }));

  // Filter by selected category
  const filteredLogs = selectedCategory === 'all' 
    ? categorizedLogs 
    : categorizedLogs.filter((log) => log.category === selectedCategory);

  // Calculate stats
  const stats = {
    total: allLogs.length,
    summary: categorizedLogs.filter((r) => r.category === 'summary').length,
    mapping: categorizedLogs.filter((r) => r.category === 'mapping').length,
    regeneration: categorizedLogs.filter((r) => r.category === 'regeneration').length,
    question: categorizedLogs.filter((r) => r.category === 'question').length,
    inputTokens: allLogs.reduce((sum, r) => sum + (r.token_usage?.prompt_tokens || 0), 0),
    outputTokens: allLogs.reduce((sum, r) => sum + (r.token_usage?.completion_tokens || 0), 0),
    totalTokens: allLogs.reduce((sum, r) => sum + (r.token_usage?.total_tokens || 0), 0),
    totalCost: allLogs.reduce((sum, r) => sum + (r.metadata?.cost_details?.total_cost || 0), 0),
  };

  return (
    <Box p={3} maxWidth="1400px" mx="auto">
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            LLM Conversation Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All LLM interactions including semantic analysis, mappings, regenerations, and Q&A
          </Typography>
        </Box>
        <IconButton 
          onClick={() => setExpandAll(!expandAll)} 
          title={expandAll ? "Collapse All" : "Expand All"}
        >
          {expandAll ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
        </IconButton>
      </Stack>

      {/* Stats Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ gap: 1 }}>
          <Chip
            icon={<AllInclusiveIcon />}
            label={`${stats.total} Total Logs`}
            variant="outlined"
            color="default"
            onClick={() => setSelectedCategory('all')}
            sx={{ fontWeight: selectedCategory === 'all' ? 700 : 400 }}
          />
          <Chip
            icon={<SummarizeIcon />}
            label={`${stats.summary} Semantic`}
            variant={selectedCategory === 'summary' ? 'filled' : 'outlined'}
            color="info"
            onClick={() => setSelectedCategory('summary')}
          />
          <Chip
            icon={<MapIcon />}
            label={`${stats.mapping} Mapping`}
            variant={selectedCategory === 'mapping' ? 'filled' : 'outlined'}
            color="primary"
            onClick={() => setSelectedCategory('mapping')}
          />
          <Chip
            icon={<RefreshIcon />}
            label={`${stats.regeneration} Regeneration`}
            variant={selectedCategory === 'regeneration' ? 'filled' : 'outlined'}
            color="warning"
            onClick={() => setSelectedCategory('regeneration')}
          />
          <Chip
            icon={<QuestionAnswerIcon />}
            label={`${stats.question} Q&A`}
            variant={selectedCategory === 'question' ? 'filled' : 'outlined'}
            color="secondary"
            onClick={() => setSelectedCategory('question')}
          />
          <Divider orientation="vertical" flexItem />
          {stats.inputTokens > 0 && (
            <Chip
              label={`Input: ${stats.inputTokens.toLocaleString()}`}
              size="small"
              variant="outlined"
              color="info"
            />
          )}
          {stats.outputTokens > 0 && (
            <Chip
              label={`Output: ${stats.outputTokens.toLocaleString()}`}
              size="small"
              variant="outlined"
              color="success"
            />
          )}
          <Chip
            label={`Total: ${stats.totalTokens.toLocaleString()} tokens`}
            size="small"
            variant="outlined"
          />
          {stats.totalCost > 0 && (
            <Chip
              label={`Cost: $${stats.totalCost < 0.01 ? stats.totalCost.toFixed(4) : stats.totalCost.toFixed(2)}`}
              size="small"
              variant="filled"
              color="warning"
            />
          )}
        </Stack>
      </Paper>

      {/* Log List */}
      {filteredLogs.length === 0 ? (
        <Box p={4} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            No logs in this category.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredLogs.map((log, index) => (
            <PromptRunCard
              key={log.log_id || log.llm_run_id || index}
              log={log}
              category={log.category}
              expanded={expandAll || expandedRunId === (log.log_id || log.llm_run_id)}
              onToggle={() => handleExpandClick(log.log_id || log.llm_run_id || String(index))}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

interface PromptRunCardProps {
  log: PromptLogEntry & { category: LogCategory };
  category: LogCategory;
  expanded: boolean;
  onToggle: () => void;
}

const PromptRunCard: React.FC<PromptRunCardProps> = ({ log, category, expanded, onToggle }) => {
  const theme = useTheme();
  const categoryConfig = getCategoryConfig(category);

  // Extract all_prompts if available (from metadata or directly)
  const allPrompts = log.metadata?.all_prompts;
  const hasConversation = allPrompts && Array.isArray(allPrompts) && allPrompts.length > 0;
  
  // Get border color based on category
  const getBorderColor = () => {
    switch (categoryConfig.color) {
      case 'info': return theme.palette.info.main;
      case 'primary': return theme.palette.primary.main;
      case 'warning': return theme.palette.warning.main;
      case 'secondary': return theme.palette.secondary.main;
      default: return theme.palette.grey[500];
    }
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        borderLeft: `4px solid ${getBorderColor()}` 
      }}
    >
      <ListItemButton onClick={onToggle} sx={{ px: 2, py: 1.5 }}>
        <ListItemText
          primary={
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ gap: 1 }}>
              <Chip
                icon={categoryConfig.icon}
                label={categoryConfig.label}
                size="small"
                color={categoryConfig.color}
                variant="filled"
              />
              <Typography variant="subtitle2" color="text.secondary">
                {log.prompt_template || log.log_type}
              </Typography>
              {log.model_id && (
                <Chip
                  label={log.model_id}
                  size="small"
                  variant="outlined"
                />
              )}
              {hasConversation && (
                <Chip
                  label={`${allPrompts?.length || 0} messages`}
                  size="small"
                  variant="outlined"
                  color="default"
                />
              )}
              {log.token_usage && log.token_usage.total_tokens > 0 && (
                <>
                  <Chip
                    label={`In: ${(log.token_usage.prompt_tokens || 0).toLocaleString()}`}
                    size="small"
                    variant="outlined"
                    color="info"
                    title="Input (prompt) tokens"
                  />
                  <Chip
                    label={`Out: ${(log.token_usage.completion_tokens || 0).toLocaleString()}`}
                    size="small"
                    variant="outlined"
                    color="success"
                    title="Output (completion) tokens"
                  />
                </>
              )}
              {(log.metadata?.cost_details?.total_cost ?? 0) > 0 && (
                <Chip
                  label={`$${(log.metadata?.cost_details?.total_cost ?? 0) < 0.01 ? log.metadata?.cost_details?.total_cost?.toFixed(4) : log.metadata?.cost_details?.total_cost?.toFixed(2)}`}
                  size="small"
                  variant="filled"
                  color="warning"
                  title={`Input: $${log.metadata?.cost_details?.input_cost?.toFixed(4) || '0'} | Output: $${log.metadata?.cost_details?.output_cost?.toFixed(4) || '0'}`}
                />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {new Date(log.created_at).toLocaleString()}
              </Typography>
            </Stack>
          }
        />
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <CardContent>
          <Stack spacing={3}>
            {/* Show multi-turn conversation if available */}
            {hasConversation ? (
              <ConversationView messages={allPrompts || []} />
            ) : (
              <>
                {/* Legacy view for single prompt/response */}
                {log.prompt_text && (
                  <MessageCard
                    role="user"
                    content={log.prompt_text}
                    label="Prompt"
                  />
                )}
                {log.response_text && (
                  <MessageCard
                    role="assistant"
                    content={log.response_text}
                    label="Response"
                  />
                )}
              </>
            )}

            {/* Token Usage Breakdown */}
            {log.token_usage && log.token_usage.total_tokens > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: theme.palette.action.hover }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Token Usage:
                  </Typography>
                  <Chip
                    label={`Prompt: ${(log.token_usage.prompt_tokens || 0).toLocaleString()}`}
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                  <Chip
                    label={`Completion: ${(log.token_usage.completion_tokens || 0).toLocaleString()}`}
                    size="small"
                    variant="outlined"
                    color="success"
                  />
                  <Chip
                    label={`Total: ${(log.token_usage.total_tokens || 0).toLocaleString()}`}
                    size="small"
                    variant="filled"
                    color="default"
                  />
                </Stack>
              </Paper>
            )}

            {/* Metadata */}
            {log.metadata && Object.keys(log.metadata).length > 0 && !log.metadata.all_prompts && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Metadata
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: theme.palette.action.hover,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflowX: 'auto',
                  }}
                >
                  <pre style={{ margin: 0 }}>
                    {JSON.stringify(log.metadata, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

interface ConversationViewProps {
  messages: any[];
}

const ConversationView: React.FC<ConversationViewProps> = ({ messages }) => {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom fontWeight={600}>
        Conversation Timeline ({messages.length} messages)
      </Typography>
      <Stack spacing={2}>
        {messages.map((msg, idx) => (
          <MessageCard
            key={idx}
            role={msg.role}
            content={msg.content}
            toolCalls={msg.tool_calls}
            toolCallId={msg.tool_call_id}
            name={msg.name}
            index={idx}
          />
        ))}
      </Stack>
    </Box>
  );
};

interface MessageCardProps {
  role: string;
  content?: string;
  toolCalls?: any[];
  toolCallId?: string;
  name?: string;
  label?: string;
  index?: number;
}

const MessageCard: React.FC<MessageCardProps> = ({ role, content, toolCalls, toolCallId, name, label, index }) => {
  const theme = useTheme();

  const getRoleConfig = () => {
    switch (role) {
      case 'system':
        return { color: theme.palette.info.main, icon: '⚙️', bgColor: theme.palette.info.light };
      case 'user':
        return { color: theme.palette.primary.main, icon: '👤', bgColor: theme.palette.primary.light };
      case 'assistant':
        return { color: theme.palette.success.main, icon: '🤖', bgColor: theme.palette.success.light };
      case 'tool':
        return { color: theme.palette.warning.main, icon: '🔧', bgColor: theme.palette.warning.light };
      default:
        return { color: theme.palette.grey[600], icon: '📝', bgColor: theme.palette.grey[300] };
    }
  };

  const config = getRoleConfig();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderLeft: `4px solid ${config.color}`,
        bgcolor: `${config.bgColor}15`,
      }}
    >
      <Stack spacing={1}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {config.icon} {label || (role ? role.toUpperCase() : 'UNKNOWN')}
          </Typography>
          {index !== undefined && (
            <Chip label={`#${index + 1}`} size="small" sx={{ height: 20 }} />
          )}
          {name && (
            <Chip label={name} size="small" variant="outlined" sx={{ height: 20 }} />
          )}
          {toolCallId && (
            <Chip label={`Tool ID: ${toolCallId.substring(0, 8)}`} size="small" sx={{ height: 20 }} />
          )}
          {content && (
            <IconButton size="small" onClick={() => navigator.clipboard.writeText(content)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* Content */}
        {content && (
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              maxHeight: '400px',
              overflowY: 'auto',
              p: 1.5,
              bgcolor: theme.palette.background.paper,
              borderRadius: 1,
            }}
          >
            {formatResponse(content)}
          </Box>
        )}

        {/* Tool Calls */}
        {toolCalls && toolCalls.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              Tool Calls ({toolCalls.length})
            </Typography>
            <Stack spacing={1}>
              {toolCalls.map((tc, tcIdx) => (
                <Paper
                  key={tcIdx}
                  sx={{
                    p: 1.5,
                    bgcolor: theme.palette.warning.light + '20',
                    border: `1px solid ${theme.palette.warning.main}40`,
                  }}
                >
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={tc.name || 'unknown'}
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        ID: {tc.id?.substring(0, 12) || 'N/A'}
                      </Typography>
                    </Stack>
                    {tc.arguments && (
                      <Box
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          bgcolor: theme.palette.background.paper,
                          p: 1,
                          borderRadius: 0.5,
                          overflowX: 'auto',
                        }}
                      >
                        <pre style={{ margin: 0 }}>
                          {typeof tc.arguments === 'string' 
                            ? tc.arguments 
                            : JSON.stringify(tc.arguments, null, 2)}
                        </pre>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default PromptLogViewer;
